import React, { useContext, useMemo, useState, useCallback } from 'react';
import { Waypoint } from 'react-waypoint';
import Slider, { SliderProps } from '@wix/da-shared-react/pkg/Slider';
import { PapiGallection } from '@wix/da-types-papi';
import FolderCard from '../FolderCard';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';
import { BiDataHook } from '@wix/da-shared-react/pkg/biLogger';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks/useFlag';
import { ScreenSizeContext } from '@wix/da-shared-react/pkg/Measured/ScreenSizeContext';

import * as sOriginal from './FolderSlider.scss';
import * as sBranched from './FolderSlider.branched.scss';

export type Props = Pick<SliderProps, 'scrollToIndex'> & {
  folders: PapiGallection[];
  selectedFolderId?: number | null;
};

export const FolderSlider: React.FC<Props> = ({
  folders,
  selectedFolderId,
}) => {
  const isMobile = useContext(MobileContext);
  const withDesignTweaks = useFlag('little_gallery_redesign');
  const s = withDesignTweaks ? sBranched : sOriginal;
  const cardWidth = useMemo(
    () =>
      parseInt(
        isMobile
          ? s['folder-card-width-mobile']
          : s['folder-card-width-desktop']
      ),
    [isMobile, s]
  );
  const isSelectedFolder = ({ folderId }) => folderId === selectedFolderId;
  const screenWidth = useContext(ScreenSizeContext);
  const cardsPerScreen = screenWidth ? Math.ceil(screenWidth / cardWidth) : 30;
  const scrollToIndex = folders.findIndex(isSelectedFolder);
  // render at least as many cards as the selected folder is
  const [renderCutOff, setRenderCutOff] = useState(
    Math.max(cardsPerScreen * 2, scrollToIndex + cardsPerScreen)
  );
  const showMore = useCallback(
    () => setRenderCutOff(cutoff => cutoff + cardsPerScreen * 2),
    [cardsPerScreen]
  );
  const foldersToRender = folders.slice(0, renderCutOff);

  return (
    <Slider
      className={s['folders-slider']}
      scrollToIndex={scrollToIndex}
      smoothInitialScroll={false}
      dataHook={BiDataHook.PROFILE_GALLECTION_FOLDER}
    >
      {foldersToRender.map((folder, index) => (
        <FolderCard
          width={cardWidth}
          className={s['folder-card']}
          key={folder.folderId}
          folder={folder}
          indexInsideParent={index}
          dataHook={BiDataHook.PROFILE_GALLECTION_FOLDER_ITEM + index}
          withCommentsCount={!withDesignTweaks || isMobile}
          showCountsWithoutCaptions={withDesignTweaks && !isMobile}
          oneLineDescription={withDesignTweaks && !isMobile}
        />
      ))}
      {renderCutOff < folders.length && (
        <Waypoint onEnter={showMore} horizontal bottomOffset="-20%" />
      )}
    </Slider>
  );
};
FolderSlider.displayName = 'FolderSlider';

export default FolderSlider;
